import type { KAPLAYCtx } from "kaplay";
import * as C from "./constants";

interface BackgroundDimensions {
  width: number;
  height: number;
}

interface OccupancyGrid {
  occupiedSpaces: boolean[][];
  gridCols: number;
  gridRows: number;
}

function calculateBackgroundDimensions(k: KAPLAYCtx): BackgroundDimensions {
  return {
    width: k.width() * C.BACKGROUND_SCALE_X,
    height: k.height() * C.BACKGROUND_SCALE_Y,
  };
}

function createBackgroundContainer(
  k: KAPLAYCtx,
  dimensions: BackgroundDimensions,
) {
  return k.add([
    k.pos(
      -dimensions.width * C.BACKGROUND_OFFSET_X_RATIO,
      -dimensions.height * C.BACKGROUND_OFFSET_Y_RATIO,
    ),
    k.z(-100),
    "background",
  ]);
}

function initializeOccupancyGrid(
  dimensions: BackgroundDimensions,
): OccupancyGrid {
  const gridCols = Math.floor(dimensions.width / C.MASONRY_GRID_SIZE);
  const gridRows = Math.floor(dimensions.height / C.MASONRY_GRID_SIZE);
  const occupiedSpaces: boolean[][] = [];

  for (let x = 0; x < gridCols; x++) {
    occupiedSpaces[x] = [];
    for (let y = 0; y < gridRows; y++) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      occupiedSpaces[x][y] = false;
    }
  }

  return { occupiedSpaces, gridCols, gridRows };
}

function canPlaceSquare(
  gridX: number,
  gridY: number,
  size: number,
  occupiedSpaces: boolean[][],
  gridCols: number,
  gridRows: number,
): boolean {
  const cellsNeeded = Math.ceil((size + C.MASONRY_GAP) / C.MASONRY_GRID_SIZE);

  for (let x = gridX; x < gridX + cellsNeeded && x < gridCols; x++) {
    for (let y = gridY; y < gridY + cellsNeeded && y < gridRows; y++) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      if (occupiedSpaces[x][y]) {
        return false;
      }
    }
  }
  return true;
}

function markSquareAsOccupied(
  gridX: number,
  gridY: number,
  size: number,
  occupiedSpaces: boolean[][],
  gridCols: number,
  gridRows: number,
): void {
  const cellsNeeded = Math.ceil((size + C.MASONRY_GAP) / C.MASONRY_GRID_SIZE);

  for (let x = gridX; x < gridX + cellsNeeded && x < gridCols; x++) {
    for (let y = gridY; y < gridY + cellsNeeded && y < gridRows; y++) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      occupiedSpaces[x][y] = true;
    }
  }
}

function placeMasonrySquares(
  k: KAPLAYCtx,
  background: any,
  grid: OccupancyGrid,
  dimensions: BackgroundDimensions,
): void {
  const { occupiedSpaces, gridCols, gridRows } = grid;

  for (let attempts = 0; attempts < C.MASONRY_ATTEMPTS; attempts++) {
    const size =
      C.MASONRY_SQUARE_SIZES[
        Math.floor(Math.random() * C.MASONRY_SQUARE_SIZES.length)
      ];
    const color =
      C.MASONRY_COLORS[Math.floor(Math.random() * C.MASONRY_COLORS.length)];

    const maxX = Math.floor(
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      (dimensions.width - size - C.MASONRY_GAP) / C.MASONRY_GRID_SIZE,
    );
    const maxY = Math.floor(
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      (dimensions.height - size - C.MASONRY_GAP) / C.MASONRY_GRID_SIZE,
    );
    const gridX = Math.floor(Math.random() * maxX);
    const gridY = Math.floor(Math.random() * maxY);

    if (
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      canPlaceSquare(gridX, gridY, size, occupiedSpaces, gridCols, gridRows)
    ) {
      markSquareAsOccupied(
        gridX,
        gridY,
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        size,
        occupiedSpaces,
        gridCols,
        gridRows,
      );

      background.add([
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        k.rect(size, size),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        k.color(...(color as [number, number, number])),
        k.pos(
          gridX * C.MASONRY_GRID_SIZE + C.MASONRY_GAP,
          gridY * C.MASONRY_GRID_SIZE + C.MASONRY_GAP,
        ),
        k.opacity(0.4),
        k.outline(2),
      ]);
    }
  }
}

export function createMasonryBackground(k: KAPLAYCtx) {
  const dimensions = calculateBackgroundDimensions(k);
  const background = createBackgroundContainer(k, dimensions);
  const grid = initializeOccupancyGrid(dimensions);

  placeMasonrySquares(k, background, grid, dimensions);
  return background;
}
